/**
 * Gift Rewards API Types
 * Types for the rewards and gifting system
 */

// ===================
// ENUMS
// ===================

export enum GiftRewardContentType {
  Image = 0,
  URL = 1,
  Text = 2,
  QR_Redeemable = 3,
  Adventure_Unlock = 4,
}

export enum DistributionType {
  Manual = 0,
  TimeInterval = 1,
  SpecificDates = 2,
  EveryNth = 3,
  Percentage = 4,
}

export enum RewardSourceType {
  Adventure = 0,
  Group = 1,
  Manual = 2,
  Transfer = 3,
}

export enum RewardInstanceStatus {
  Active = 0,
  Expired = 1,
  FullyRedeemed = 2,
  Revoked = 3,
}

export enum ExpirationType {
  Never = 0,
  DaysFromClaim = 1,
  FixedDate = 2,
}

// ===================
// HELPER FUNCTIONS
// ===================

export function getContentTypeName(type: GiftRewardContentType): string {
  switch (type) {
    case GiftRewardContentType.Image:
      return 'Image'
    case GiftRewardContentType.URL:
      return 'Link'
    case GiftRewardContentType.Text:
      return 'Text'
    case GiftRewardContentType.QR_Redeemable:
      return 'QR Redeemable'
    case GiftRewardContentType.Adventure_Unlock:
      return 'Adventure Unlock'
    default:
      return 'Unknown'
  }
}

export function getDistributionTypeName(type: DistributionType): string {
  switch (type) {
    case DistributionType.TimeInterval:
      return 'Time Interval'
    case DistributionType.SpecificDates:
      return 'Specific Dates'
    case DistributionType.EveryNth:
      return 'Every Nth'
    case DistributionType.Percentage:
      return 'Percentage'
    case DistributionType.Manual:
      return 'Manual'
    default:
      return 'Unknown'
  }
}

export function getSourceTypeName(type: RewardSourceType): string {
  switch (type) {
    case RewardSourceType.Adventure:
      return 'Adventure'
    case RewardSourceType.Group:
      return 'Group'
    case RewardSourceType.Manual:
      return 'Manual'
    case RewardSourceType.Transfer:
      return 'Transfer'
    default:
      return 'Unknown'
  }
}

export function getInstanceStatusName(status: RewardInstanceStatus): string {
  switch (status) {
    case RewardInstanceStatus.Active:
      return 'Active'
    case RewardInstanceStatus.Expired:
      return 'Expired'
    case RewardInstanceStatus.FullyRedeemed:
      return 'Redeemed'
    case RewardInstanceStatus.Revoked:
      return 'Revoked'
    default:
      return 'Unknown'
  }
}

export function getExpirationTypeName(type: ExpirationType): string {
  switch (type) {
    case ExpirationType.Never:
      return 'Never Expires'
    case ExpirationType.DaysFromClaim:
      return 'Days from Claim'
    case ExpirationType.FixedDate:
      return 'Fixed Date'
    default:
      return 'Unknown'
  }
}

// ===================
// TEMPLATE DTOs
// ===================

/** Summary of a reward template (embedded in instance) */
export interface GiftRewardTemplateSummaryDto {
  id: string
  title: string | null
  description: string | null
  contentType: GiftRewardContentType
  contentTypeName: string | null
  isTransferable: boolean
  usesPerInstance: number | null
}

/** Full reward template details */
export interface GiftRewardTemplateDto {
  id: string
  createdByUserId: string | null
  createdByDisplayName: string | null
  groupId: string
  groupName: string | null
  title: string | null
  description: string | null
  isActive: boolean
  contentType: GiftRewardContentType
  contentTypeName: string | null
  content: unknown | null
  distributionType: DistributionType
  distributionTypeName: string | null
  distributionConfig: Record<string, unknown> | null
  totalLimit: number | null
  dailyLimit: number | null
  weeklyLimit: number | null
  yearlyLimit: number | null
  usesPerInstance: number | null
  isTransferable: boolean
  allowPostExpirationRedemption: boolean
  expirationType: ExpirationType
  expirationTypeName: string | null
  expirationDays: number | null
  fixedExpirationDate: string | null
  authorizedScannerGroupId: string | null
  authorizedScannerGroupName: string | null
  createdAt: string
  updatedAt: string
  totalDistributed: number
  activeInstances: number
}

/** Paginated template response */
export interface GiftRewardTemplatePagedResult {
  items: GiftRewardTemplateDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// ===================
// INSTANCE DTOs
// ===================

/** Source information for a reward instance */
export interface RewardSourceDto {
  adventureId: string | null
  adventureThumbnail: string | null
  adventureTitle: string | null
  adventureTypeId: string | null
  adventureTypeName: string | null
  adventureDescription: string | null
  groupId: string | null
  groupThumbnail: string | null
  groupName: string | null
  groupDescription: string | null
}

/** User's reward instance (full details) */
export interface GiftRewardInstanceDto {
  id: string
  uniqueCode: string | null
  templateId: string
  template: GiftRewardTemplateSummaryDto
  currentOwnerUserId: string | null
  currentOwnerDisplayName: string | null
  originalRecipientUserId: string | null
  originalRecipientDisplayName: string | null
  sourceType: RewardSourceType
  sourceTypeName: string | null
  sourceId: string | null
  source: RewardSourceDto | null
  status: RewardInstanceStatus
  statusName: string | null
  remainingUses: number | null
  transferCount: number
  expiresAt: string | null
  isExpired: boolean
  content: unknown | null
  displayKey: string | null
  claimedAt: string
  updatedAt: string
}

/** User reward (simplified for list views) */
export interface UserRewardDto {
  id: string
  title: string | null
  description: string | null
  contentType: GiftRewardContentType
  contentTypeName: string | null
  status: RewardInstanceStatus
  statusName: string | null
  remainingUses: number | null
  isTransferable: boolean
  expiresAt: string | null
  isExpired: boolean
  allowPostExpirationRedemption: boolean
  content: unknown | null
  displayKey: string | null
  claimedAt: string
}

/** Paginated user rewards response */
export interface UserRewardPagedResult {
  items: UserRewardDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// ===================
// QR CODE DTOs
// ===================

export interface RewardQrCodeDto {
  instanceId: string
  qrData: string | null
  title: string | null
  remainingUses: number | null
  expiresAt: string | null
}

// ===================
// STATS DTOs
// ===================

export interface DistributionBySourceDto {
  adventure: number
  group: number
  direct: number
  system: number
}

export interface CurrentHolderDto {
  userId: string | null
  displayName: string | null
  claimedAt: string
  remainingUses: number | null
  status: RewardInstanceStatus
}

export interface TemplateStatsDto {
  templateId: string
  totalDistributed: number
  totalRedeemedUses: number
  activeInstances: number
  expiredInstances: number
  fullyRedeemedInstances: number
  distributionBySource: DistributionBySourceDto
  currentHolders: CurrentHolderDto[] | null
}

// ===================
// HISTORY DTOs
// ===================

export interface RewardRedemptionLogDto {
  id: string
  instanceId: string
  scannerUserId: string | null
  scannerDisplayName: string | null
  scannerGroupId: string | null
  scannerGroupName: string | null
  latitude: number | null
  longitude: number | null
  usesConsumed: number
  remainingAfter: number | null
  wasExpiredOverride: boolean
  redeemedAt: string
}

export interface RewardRedemptionLogPagedResult {
  items: RewardRedemptionLogDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface RewardTransferHistoryDto {
  id: string
  instanceId: string
  fromUserId: string | null
  fromUserDisplayName: string | null
  toUserId: string | null
  toUserDisplayName: string | null
  transferredAt: string
}

export interface RewardTransferHistoryPagedResult {
  items: RewardTransferHistoryDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// ===================
// VALIDATION & REDEMPTION
// ===================

export interface RewardOwnerDto {
  userId: string
  displayName: string
  profilePicture: string | null
  avatar: string | null
  location: string | null
  homeTown: string | null
  bio: string | null
  isConnected: boolean | null
  isOnline: boolean
  connectionStatus: string | null
  blockedAt: string | null
  status: number
  rank: {
    tier: number
    name: string
    pointsRequired: number
    description: string
    badge: string
    cssClass: string
    themeFusion: string
  } | null
}

export interface ValidateRewardCodeResponse {
  valid: boolean
  canRedeem: boolean
  message: string | null
  instanceId: string | null
  title: string | null
  owner: RewardOwnerDto | null
  ownerDisplayName: string | null // Legacy field for backwards compatibility
  remainingUses: number | null
  isExpired: boolean
  canOverrideExpiration: boolean
}

export interface RedeemRewardRequest {
  uniqueCode: string
  latitude?: number | null
  longitude?: number | null
  forceExpiredRedemption?: boolean
}

export interface RedeemRewardResponse {
  success: boolean
  message: string | null
  instance: GiftRewardInstanceDto | null
  wasExpiredOverride: boolean
}

// ===================
// TRANSFER
// ===================

export interface TransferRewardRequest {
  instanceId: string
  toUserId: string
}

export interface TransferRewardResponse {
  success: boolean
  message: string | null
  instance: GiftRewardInstanceDto | null
}

export interface CanTransferResponse {
  canTransfer: boolean
  reason: string | null
}

// ===================
// DISTRIBUTION CONFIG TYPES
// ===================

export type IntervalUnit = 'hour' | 'day' | 'week' | 'month' | 'year'

/** TimeInterval distribution: Automatically distribute on a schedule */
export interface TimeIntervalConfig {
  intervalAmount: number
  intervalUnit: IntervalUnit
  quantityPerInterval: number
}

/** SpecificDates distribution: Distribute on specific dates */
export interface SpecificDatesConfig {
  dates: string[] // ISO date strings
  quantityPerDate: number
}

/** EveryNth distribution: Every Nth completion gets a reward */
export interface EveryNthConfig {
  nthValue: number
}

/** Percentage distribution: Random percentage of users get rewards */
export interface PercentageConfig {
  percentage: number
}

export type DistributionConfig =
  | TimeIntervalConfig
  | SpecificDatesConfig
  | EveryNthConfig
  | PercentageConfig
  | null

// ===================
// CREATE/UPDATE REQUESTS
// ===================

export interface CreateGiftRewardTemplateRequest {
  groupId?: string | null
  title: string
  description?: string | null
  contentType: GiftRewardContentType
  content?: unknown | null
  distributionType: DistributionType
  distributionConfig?: Record<string, unknown> | null
  totalLimit?: number | null
  dailyLimit?: number | null
  weeklyLimit?: number | null
  yearlyLimit?: number | null
  usesPerInstance?: number | null
  isTransferable?: boolean
  allowPostExpirationRedemption?: boolean
  expirationType?: ExpirationType
  expirationDays?: number | null
  fixedExpirationDate?: string | null
  authorizedScannerGroupId?: string | null
}

export interface UpdateGiftRewardTemplateRequest {
  title?: string | null
  description?: string | null
  isActive?: boolean | null
  contentType?: GiftRewardContentType
  content?: unknown | null
  totalLimit?: number | null
  dailyLimit?: number | null
  weeklyLimit?: number | null
  yearlyLimit?: number | null
  authorizedScannerGroupId?: string | null
}

// ===================
// DISTRIBUTION
// ===================

export interface DistributeRewardRequest {
  userId?: string | null
  userIds?: string[] | null
  sourceType: RewardSourceType
  sourceId?: string | null
}

export interface DistributeRewardResponse {
  success: boolean
  message: string | null
  distributedCount: number
  instances: GiftRewardInstanceDto[] | null
  failedUserIds: string[] | null
}
